import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ApiClientError } from '../api/http'
import { listChannelConnections } from '../channels/channelsApi'
import type { ChannelConnection, ChannelProvider } from '../channels/types'
import { Button, Card, ChannelDot, Icon, I, MOBILE_SINGLE_COLUMN, Pill, TEXT_WRAP_STYLE, Toggle, gridColumns, mobileButtonStyle } from '../components/ui'
import { useIsMobileViewport } from '../hooks/useIsMobileViewport'
import { listProducts } from '../products/productsApi'
import type { ProductListItem } from '../products/types'
import { listSourceAuthorities } from '../source/sourceApi'
import type { SourceAuthority } from '../source/types'
import { listStockAllocationRules, listStockAllocationTargetLocations, saveStockAllocationRule } from '../stockAllocation/stockAllocationApi'
import type { ChannelStockAllocationExcludedProduct, ChannelStockAllocationRule, ChannelStockAllocationRuleUpdateRequest, StockAllocationTargetLocation } from '../stockAllocation/types'
import { listWarehouses } from '../warehouses/warehousesApi'
import type { Warehouse } from '../warehouses/types'
import { StockAllocationActivity } from './StockAllocationActivity'

interface StockAllocationRulesPageProps {
  accessToken: string
  onBack: () => void
  initialTab?: 'rules' | 'activity'
  onOpenProduct?: (productId: number) => void
}

interface RuleDraft {
  enabled: boolean
  allocationPercent: string
  targetExternalLocationId: string
  warehouseIds: number[]
  excludedProducts: ChannelStockAllocationExcludedProduct[]
}

interface ExclusionSearchState {
  query: string
  loading: boolean
  error: unknown | null
  results: ProductListItem[]
  hasSearched: boolean
}

type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral'

const PROVIDER_DOT_NAMES: Record<ChannelProvider, string> = {
  ZID: 'Zid',
  SALLA: 'Salla',
  AMAZON: 'Amazon',
  TRENDYOL: 'Trendyol',
  NOON: 'Noon',
}

function statusTone(status: ChannelConnection['status']): PillTone {
  if (status === 'CONNECTED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'AUTH_EXPIRED' || status === 'ERROR') return 'danger'
  return 'neutral'
}

function errorMessageKey(error: unknown): string {
  if (error instanceof ApiClientError) {
    return `stockAllocation.errors.codes.${error.code}`
  }
  return 'stockAllocation.errors.codes.REQUEST_FAILED'
}

const defaultExclusionSearchState: ExclusionSearchState = {
  query: '',
  loading: false,
  error: null,
  results: [],
  hasSearched: false,
}

function normalizeExcludedProducts(rule: ChannelStockAllocationRule | undefined): ChannelStockAllocationExcludedProduct[] {
  const byId = new Map<number, ChannelStockAllocationExcludedProduct>()
  rule?.excludedProducts?.forEach(product => byId.set(product.id, product))
  rule?.excludedProductIds?.forEach(id => {
    if (!byId.has(id)) byId.set(id, { id, sku: '', name: '', status: 'ACTIVE' })
  })
  return Array.from(byId.values()).sort((a, b) => a.id - b.id)
}

function draftFromRule(rule: ChannelStockAllocationRule | undefined): RuleDraft {
  return {
    enabled: rule?.enabled ?? false,
    allocationPercent: rule?.allocationPercent == null ? '100' : String(rule.allocationPercent),
    targetExternalLocationId: rule?.targetExternalLocationId ?? '',
    warehouseIds: rule?.warehouseIds ?? [],
    excludedProducts: normalizeExcludedProducts(rule),
  }
}

function sortedWarehouseIds(ids: number[]): number[] {
  return Array.from(new Set(ids)).sort((a, b) => a - b)
}

function sortedProductIds(products: ChannelStockAllocationExcludedProduct[]): number[] {
  return Array.from(new Set(products.map(product => product.id))).sort((a, b) => a - b)
}

function exclusionFromProduct(product: ProductListItem): ChannelStockAllocationExcludedProduct {
  return {
    id: product.id,
    sku: product.sku,
    name: product.name,
    status: product.status,
  }
}

function optionalPercent(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isInteger(parsed) ? parsed : null
}

function enabledPercent(value: string): number | null {
  const parsed = optionalPercent(value)
  if (parsed == null || parsed < 0 || parsed > 100) return null
  return parsed
}

export function StockAllocationRulesPage({ accessToken, onBack, initialTab = 'rules', onOpenProduct }: StockAllocationRulesPageProps) {
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobileViewport()
  const [connections, setConnections] = useState<ChannelConnection[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [authorities, setAuthorities] = useState<SourceAuthority[]>([])
  const [rules, setRules] = useState<ChannelStockAllocationRule[]>([])
  const [targetLocations, setTargetLocations] = useState<StockAllocationTargetLocation[]>([])
  const [drafts, setDrafts] = useState<Record<number, RuleDraft>>({})
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [saveErrors, setSaveErrors] = useState<Record<number, unknown>>({})
  const [savedId, setSavedId] = useState<number | null>(null)
  const [exclusionSearch, setExclusionSearch] = useState<Record<number, ExclusionSearchState>>({})
  const [activeTab, setActiveTab] = useState<'rules' | 'activity'>(initialTab)

  useEffect(() => {
    let cancelled = false
    // Intentional reset on accessToken change so this data page shows a fresh loading state.
    setLoading(true)
    setLoadError(null)
    setExclusionSearch({})

    Promise.all([
      listChannelConnections(accessToken),
      listWarehouses(accessToken),
      listSourceAuthorities(accessToken),
      listStockAllocationRules(accessToken),
      listStockAllocationTargetLocations(accessToken),
    ])
      .then(([nextConnections, nextWarehouses, nextAuthorities, nextRules, nextTargetLocations]) => {
        if (cancelled) return
        const rulesByConnectionId = new Map(nextRules.map(rule => [rule.channelConnectionId, rule]))
        setConnections(nextConnections)
        setWarehouses(nextWarehouses)
        setAuthorities(nextAuthorities)
        setRules(nextRules)
        setTargetLocations(nextTargetLocations)
        setDrafts(Object.fromEntries(nextConnections.map(connection => [connection.id, draftFromRule(rulesByConnectionId.get(connection.id))])))
      })
      .catch(caught => {
        if (!cancelled) setLoadError(caught)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [accessToken])

  const rulesByConnectionId = useMemo(() => new Map(rules.map(rule => [rule.channelConnectionId, rule])), [rules])
  const warehouseById = useMemo(() => new Map(warehouses.map(warehouse => [warehouse.id, warehouse])), [warehouses])
  const stockAuthority = authorities.find(authority => authority.domain === 'STOCK')
  const mobileActionStyle = mobileButtonStyle(isMobile)

  const warehouseTotals = useMemo(() => {
    const totals = new Map<number, number>()
    Object.values(drafts).forEach(draft => {
      if (!draft.enabled) return
      const percent = enabledPercent(draft.allocationPercent)
      if (percent == null) return
      draft.warehouseIds.forEach(warehouseId => {
        totals.set(warehouseId, (totals.get(warehouseId) ?? 0) + percent)
      })
    })
    return totals
  }, [drafts])

  const overAllocatedWarehouses = warehouses.filter(warehouse => (warehouseTotals.get(warehouse.id) ?? 0) > 100)

  const isStockSource = (connection: ChannelConnection): boolean =>
    stockAuthority?.ownerType === 'CHANNEL_CONNECTION' && stockAuthority.channelConnectionId === connection.id

  const isSupportedAllocationProvider = (provider: ChannelProvider): boolean => provider === 'ZID' || provider === 'SALLA' || provider === 'NOON'

  const canConfigure = (connection: ChannelConnection): boolean =>
    isSupportedAllocationProvider(connection.provider) && connection.status === 'CONNECTED' && !isStockSource(connection)

  const availabilityKey = (connection: ChannelConnection): string => {
    if (!isSupportedAllocationProvider(connection.provider)) return 'stockAllocation.availability.unsupported'
    if (connection.status !== 'CONNECTED') return 'stockAllocation.availability.disconnected'
    if (isStockSource(connection)) return 'stockAllocation.availability.sourceOwned'
    if (connection.provider === 'NOON') return 'stockAllocation.availability.noonReady'
    return 'stockAllocation.availability.ready'
  }

  const updateDraft = (connectionId: number, updater: (draft: RuleDraft) => RuleDraft) => {
    setDrafts(current => ({
      ...current,
      [connectionId]: updater(current[connectionId] ?? draftFromRule(rulesByConnectionId.get(connectionId))),
    }))
    setSavedId(null)
    setSaveErrors(current => {
      const next = { ...current }
      delete next[connectionId]
      return next
    })
  }

  const toggleWarehouse = (connectionId: number, warehouseId: number) => {
    updateDraft(connectionId, draft => {
      const selected = draft.warehouseIds.includes(warehouseId)
      return {
        ...draft,
        warehouseIds: selected
          ? draft.warehouseIds.filter(id => id !== warehouseId)
          : sortedWarehouseIds([...draft.warehouseIds, warehouseId]),
      }
    })
  }

  const validatePayload = (connection: ChannelConnection, draft: RuleDraft): ChannelStockAllocationRuleUpdateRequest => {
    const percent = optionalPercent(draft.allocationPercent)
    const targetExternalLocationId = draft.targetExternalLocationId.trim()
    const enablingBlocked = draft.enabled && !canConfigure(connection)
    if (enablingBlocked) throw new ApiClientError(400, { code: 'CHANNEL_STOCK_ALLOCATION_UNAVAILABLE' })
    if (draft.enabled && (percent == null || percent < 0 || percent > 100)) {
      throw new ApiClientError(400, { code: 'CHANNEL_STOCK_ALLOCATION_PERCENT_INVALID' })
    }
    if (draft.enabled && draft.warehouseIds.length === 0) {
      throw new ApiClientError(400, { code: 'CHANNEL_STOCK_ALLOCATION_WAREHOUSES_REQUIRED' })
    }
    if (draft.enabled && connection.provider === 'SALLA' && !targetExternalLocationId) {
      throw new ApiClientError(400, { code: 'CHANNEL_STOCK_ALLOCATION_TARGET_LOCATION_REQUIRED' })
    }
    return {
      enabled: draft.enabled,
      allocationPercent: draft.enabled ? percent : percent == null || percent < 0 || percent > 100 ? null : percent,
      ...(connection.provider === 'SALLA' ? { targetExternalLocationId: targetExternalLocationId || null } : {}),
      warehouseIds: sortedWarehouseIds(draft.warehouseIds),
      excludedProductIds: sortedProductIds(draft.excludedProducts),
    }
  }

  const saveRule = async (connection: ChannelConnection) => {
    const draft = drafts[connection.id] ?? draftFromRule(rulesByConnectionId.get(connection.id))
    setSavingId(connection.id)
    setSavedId(null)
    setSaveErrors(current => {
      const next = { ...current }
      delete next[connection.id]
      return next
    })
    try {
      const payload = validatePayload(connection, draft)
      const saved = await saveStockAllocationRule(accessToken, connection.id, payload)
      setRules(current => [saved, ...current.filter(rule => rule.channelConnectionId !== connection.id)])
      setDrafts(current => ({ ...current, [connection.id]: draftFromRule(saved) }))
      setSavedId(connection.id)
    } catch (caught) {
      setSaveErrors(current => ({ ...current, [connection.id]: caught }))
    } finally {
      setSavingId(null)
    }
  }

  const searchStateFor = (connectionId: number): ExclusionSearchState => exclusionSearch[connectionId] ?? defaultExclusionSearchState

  const updateExclusionQuery = (connectionId: number, query: string) => {
    setExclusionSearch(current => ({
      ...current,
      [connectionId]: {
        ...(current[connectionId] ?? defaultExclusionSearchState),
        query,
        loading: false,
        error: null,
        results: [],
        hasSearched: false,
      },
    }))
  }

  const searchExclusions = async (connection: ChannelConnection) => {
    const query = searchStateFor(connection.id).query.trim()
    if (!query) {
      setExclusionSearch(current => ({
        ...current,
        [connection.id]: { ...(current[connection.id] ?? defaultExclusionSearchState), query: '', results: [], error: null, hasSearched: true },
      }))
      return
    }

    setExclusionSearch(current => ({
      ...current,
      [connection.id]: { ...(current[connection.id] ?? defaultExclusionSearchState), query, loading: true, error: null, hasSearched: true },
    }))

    try {
      const page = await listProducts(accessToken, {
        query,
        size: 8,
        ...(connection.provider === 'NOON' ? {} : {
          listedProvider: connection.provider,
          listedChannelConnectionId: connection.id,
        }),
      })
      setExclusionSearch(current => {
        const existing = current[connection.id] ?? defaultExclusionSearchState
        if (existing.query.trim() !== query) return current
        return {
          ...current,
          [connection.id]: { ...existing, loading: false, error: null, results: page.items, hasSearched: true },
        }
      })
    } catch (caught) {
      setExclusionSearch(current => {
        const existing = current[connection.id] ?? defaultExclusionSearchState
        if (existing.query.trim() !== query) return current
        return {
          ...current,
          [connection.id]: { ...existing, loading: false, error: caught, results: [], hasSearched: true },
        }
      })
    }
  }

  const addExcludedProduct = (connectionId: number, product: ProductListItem) => {
    updateDraft(connectionId, draft => {
      if (draft.excludedProducts.some(selected => selected.id === product.id)) return draft
      return { ...draft, excludedProducts: [...draft.excludedProducts, exclusionFromProduct(product)].sort((a, b) => a.id - b.id) }
    })
  }

  const removeExcludedProduct = (connectionId: number, productId: number) => {
    updateDraft(connectionId, draft => ({
      ...draft,
      excludedProducts: draft.excludedProducts.filter(product => product.id !== productId),
    }))
  }

  const excludedProductName = (product: ChannelStockAllocationExcludedProduct): string =>
    product.name || String(t('stockAllocation.exclusions.unknownProduct', { id: product.id }))

  const selectedWarehouseNames = (draft: RuleDraft): string => {
    if (draft.warehouseIds.length === 0) return String(t('stockAllocation.emptyWarehouses'))
    return draft.warehouseIds
      .map(id => warehouseById.get(id)?.name ?? String(t('stockAllocation.unknownWarehouse', { id })))
      .join(' · ')
  }

  return (
    <div>
      <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'flex-start', gap: 16, marginBottom: 20 }}>
        <div style={{ minWidth: 0 }}>
          <Button kind="ghost" size="xs" icon={i18n.dir() === 'rtl' ? I.arrowR : I.arrowL} onClick={onBack} style={{ marginBottom: 10, ...mobileActionStyle }}>
            {t('stockAllocation.actions.back')}
          </Button>
          <h2 style={{ margin: 0, fontSize: 22, color: 'var(--brand-900)' }}>{t('stockAllocation.workspaceTitle')}</h2>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: 'var(--fg-muted)', maxWidth: 760, lineHeight: 1.55, ...TEXT_WRAP_STYLE }}>
            {t('stockAllocation.workspaceDescription')}
          </p>
        </div>
      </div>

      <div role="tablist" aria-label={String(t('stockAllocation.tabs.label'))} style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-subtle)', marginBottom: 20 }}>
        {(['rules', 'activity'] as const).map(tab => (
          <button key={tab} type="button" role="tab" aria-selected={activeTab === tab} onClick={() => setActiveTab(tab)} style={{ border: 'none', borderBottom: activeTab === tab ? '2px solid var(--brand-500)' : '2px solid transparent', background: 'transparent', color: activeTab === tab ? 'var(--brand-700)' : 'var(--fg-muted)', padding: '10px 14px', fontFamily: 'inherit', fontWeight: activeTab === tab ? 800 : 700, cursor: 'pointer' }}>
            {t(`stockAllocation.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === 'activity' ? (
        <StockAllocationActivity accessToken={accessToken} onOpenProduct={onOpenProduct} />
      ) : (
        <>

      {loading && (
        <Card padding={22} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--fg-muted)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.refresh}</Icon>
            {t('stockAllocation.loading')}
          </div>
        </Card>
      )}

      {loadError !== null && (
        <Card padding={22} style={{ marginBottom: 16, borderColor: 'var(--danger-100)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--danger)', fontSize: 13, fontWeight: 700 }}>
            <Icon size={16}>{I.alert}</Icon>
            {String(t(errorMessageKey(loadError), { defaultValue: String(t('stockAllocation.errors.codes.REQUEST_FAILED')) }))}
          </div>
        </Card>
      )}

      {!loading && connections.length === 0 && loadError === null && (
        <Card padding={22}>
          <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--fg)' }}>{t('stockAllocation.empty.title')}</div>
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 4 }}>{t('stockAllocation.empty.body')}</div>
        </Card>
      )}

      {overAllocatedWarehouses.length > 0 && (
        <Card padding={16} style={{ marginBottom: 16, borderColor: 'var(--warning-100)', background: 'var(--warning-50)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 10, alignItems: 'start' }}>
            <Icon size={18} color="var(--warning)">{I.alert}</Icon>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--fg)' }}>{t('stockAllocation.overAllocation.title')}</div>
              <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 3, lineHeight: 1.5 }}>
                {t('stockAllocation.overAllocation.body', {
                  warehouses: overAllocatedWarehouses.map(warehouse => `${warehouse.name} (${warehouseTotals.get(warehouse.id)}%)`).join(' · '),
                })}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gap: 14 }}>
        {connections.map(connection => {
          const draft = drafts[connection.id] ?? draftFromRule(rulesByConnectionId.get(connection.id))
          const existingRule = rulesByConnectionId.get(connection.id)
          const eligible = canConfigure(connection)
          const toggleDisabled = !eligible && !existingRule?.enabled && !draft.enabled
          const canDisableExistingIneligibleRule = !eligible && Boolean(existingRule?.enabled) && !draft.enabled
          const saveDisabled = savingId === connection.id || (!eligible && !canDisableExistingIneligibleRule)
          const cardError = saveErrors[connection.id]
          const cardSaved = savedId === connection.id
          const selectedOverAllocated = draft.warehouseIds.filter(id => (warehouseTotals.get(id) ?? 0) > 100)
          const productSearch = searchStateFor(connection.id)
          const exclusionControlsDisabled = !eligible

          return (
            <Card key={connection.id} padding={20} style={{ borderColor: eligible ? 'var(--border-subtle)' : 'var(--ink-200)' }}>
              <div style={{ display: isMobile ? 'grid' : 'flex', justifyContent: 'space-between', gap: 14, alignItems: isMobile ? 'stretch' : 'flex-start', marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', gap: 12, alignItems: 'start', minWidth: 0 }}>
                  <ChannelDot name={PROVIDER_DOT_NAMES[connection.provider]} size={38} dim={!eligible} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{connection.displayName}</div>
                      <Pill tone={statusTone(connection.status)} size="sm" dot>{t(`channels.status.${connection.status}`)}</Pill>
                      <Pill tone={draft.enabled ? 'success' : 'neutral'} size="sm">{t(draft.enabled ? 'stockAllocation.status.enabled' : 'stockAllocation.status.disabled')}</Pill>
                    </div>
                    <div style={{ color: 'var(--fg-muted)', fontSize: 12.5, marginTop: 3 }}>{t(`channels.providers.${connection.provider}`)}</div>
                    <div style={{ fontSize: 12.5, color: eligible ? 'var(--fg-muted)' : 'var(--warning)', fontWeight: eligible ? 500 : 700, marginTop: 6, lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>
                      {t(availabilityKey(connection), { provider: t(`channels.providers.${connection.provider}`), status: t(`channels.status.${connection.status}`) })}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
                  <span style={{ fontSize: 12.5, color: 'var(--fg-muted)', fontWeight: 700 }}>{t('stockAllocation.fields.enabled')}</span>
                  <Toggle
                    on={draft.enabled}
                    disabled={toggleDisabled}
                    ariaLabel={String(t('stockAllocation.fields.enabledAria', { name: connection.displayName }))}
                    onChange={(enabled) => updateDraft(connection.id, current => ({ ...current, enabled }))}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: gridColumns(isMobile, 'minmax(180px, 220px) minmax(0, 1fr)'), gap: 16, alignItems: 'start' }}>
                <label style={{ display: 'block' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>{t('stockAllocation.fields.percent')}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center' }}>
                    <input
                      aria-label={String(t('stockAllocation.fields.percentAria', { name: connection.displayName }))}
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={draft.allocationPercent}
                      disabled={!eligible && !existingRule?.enabled}
                      onChange={event => updateDraft(connection.id, current => ({ ...current, allocationPercent: event.target.value }))}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none', borderStartEndRadius: 0, borderEndEndRadius: 0,
                      }}
                    />
                    <span style={{ paddingBlock: 10, paddingInline: 12, border: '1px solid var(--border-default)', borderInlineStart: 0, borderStartEndRadius: 9, borderEndEndRadius: 9, color: 'var(--fg-muted)', fontSize: 13, background: 'var(--ink-50)' }}>
                      {t('stockAllocation.fields.percentSuffix')}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 6, lineHeight: 1.45 }}>
                    {t('stockAllocation.fields.percentHint')}
                  </div>
                </label>

                {connection.provider === 'SALLA' && (
                  <label style={{ display: 'grid', gap: 6, gridColumn: isMobile ? undefined : '1 / -1' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)' }}>{t('stockAllocation.fields.targetSallaBranch')}</span>
                    <select
                      aria-label={String(t('stockAllocation.fields.targetSallaBranchAria', { name: connection.displayName }))}
                      value={draft.targetExternalLocationId}
                      disabled={!eligible && !existingRule?.enabled}
                      onChange={event => updateDraft(connection.id, current => ({ ...current, targetExternalLocationId: event.target.value }))}
                      style={{
                        width: '100%', minWidth: 0, paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit',
                        color: 'var(--fg)', background: (!eligible && !existingRule?.enabled) ? 'var(--ink-100)' : 'white', border: '1px solid var(--border-default)',
                        borderRadius: 9, outline: 'none', boxSizing: 'border-box',
                      }}
                    >
                      <option value="">{t('stockAllocation.fields.targetSallaBranchPlaceholder')}</option>
                      {draft.targetExternalLocationId && !targetLocations.some(location => location.channelConnectionId === connection.id && location.externalLocationId === draft.targetExternalLocationId) && (
                        <option value={draft.targetExternalLocationId} disabled>{t('stockAllocation.fields.targetSallaBranchUnavailable')}</option>
                      )}
                      {targetLocations.filter(location => location.channelConnectionId === connection.id).map(location => (
                        <option key={location.externalLocationId} value={location.externalLocationId}>{location.name}</option>
                      ))}
                    </select>
                    <span style={{ fontSize: 12, color: 'var(--fg-subtle)', lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>
                      {t(targetLocations.some(location => location.channelConnectionId === connection.id)
                        ? 'stockAllocation.fields.targetSallaBranchHint'
                        : 'stockAllocation.fields.targetSallaBranchEmpty')}
                    </span>
                  </label>
                )}

                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>{t('stockAllocation.fields.warehouses')}</div>
                  {warehouses.length === 0 ? (
                    <div style={{ padding: 12, borderRadius: 12, background: 'var(--ink-50)', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                      {t('stockAllocation.noWarehouses')}
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                      {warehouses.map(warehouse => {
                        const checked = draft.warehouseIds.includes(warehouse.id)
                        const total = warehouseTotals.get(warehouse.id) ?? 0
                        return (
                          <label key={warehouse.id} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', gap: 8, alignItems: 'start', padding: 10, borderRadius: 12, border: checked ? '1.5px solid var(--brand-400)' : '1px solid var(--border-subtle)', background: checked ? 'var(--brand-100)' : 'white', cursor: eligible ? 'pointer' : 'not-allowed' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!eligible && !existingRule?.enabled}
                              onChange={() => toggleWarehouse(connection.id, warehouse.id)}
                              style={{ marginBlockStart: 2 }}
                            />
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{warehouse.name}</span>
                              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 2, ...TEXT_WRAP_STYLE }}>{warehouse.code}</span>
                              {checked && total > 100 && (
                                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--warning)', fontWeight: 800, marginTop: 4 }}>
                                  {t('stockAllocation.overAllocation.warehouseTotal', { total })}
                                </span>
                              )}
                            </span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 16, padding: 14, borderRadius: 14, border: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>
                <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)' }}>{t('stockAllocation.exclusions.title')}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>{t(connection.provider === 'NOON' ? 'stockAllocation.exclusions.noonHint' : 'stockAllocation.exclusions.hint')}</div>
                </div>

                {draft.excludedProducts.length === 0 ? (
                  <div style={{ padding: 12, borderRadius: 12, background: 'white', border: '1px dashed var(--border-default)', color: 'var(--fg-muted)', fontSize: 12.5 }}>
                    {t('stockAllocation.exclusions.empty')}
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                    {draft.excludedProducts.map(product => (
                      <div key={product.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: 10, borderRadius: 12, background: 'white', border: '1px solid var(--border-subtle)' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{excludedProductName(product)}</div>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 3 }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>{product.sku || t('stockAllocation.exclusions.unknownSku')}</span>
                            <Pill tone="neutral" size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                          </div>
                        </div>
                        <button
                          type="button"
                          disabled={exclusionControlsDisabled}
                          aria-label={String(t('stockAllocation.exclusions.removeAria', { name: excludedProductName(product) }))}
                          onClick={() => removeExcludedProduct(connection.id, product.id)}
                          style={{
                            justifySelf: isMobile ? 'stretch' : 'end',
                            fontFamily: 'inherit', fontSize: 12, fontWeight: 800, color: 'var(--danger)', background: 'white',
                            border: '1px solid var(--danger-100)', borderRadius: 10, paddingBlock: 7, paddingInline: 10,
                            cursor: exclusionControlsDisabled ? 'not-allowed' : 'pointer', opacity: exclusionControlsDisabled ? 0.5 : 1,
                          }}
                        >
                          {t('stockAllocation.exclusions.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 8, alignItems: 'end', marginTop: 12 }}>
                  <label style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--fg)' }}>{t(connection.provider === 'NOON' ? 'stockAllocation.exclusions.noonSearchLabel' : 'stockAllocation.exclusions.searchLabel')}</span>
                    <input
                      aria-label={String(t('stockAllocation.exclusions.searchAria', { name: connection.displayName }))}
                      type="search"
                      value={productSearch.query}
                      disabled={exclusionControlsDisabled}
                      placeholder={String(t('stockAllocation.exclusions.searchPlaceholder'))}
                      onChange={event => updateExclusionQuery(connection.id, event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          void searchExclusions(connection)
                        }
                      }}
                      style={{
                        width: '100%', paddingBlock: 10, paddingInline: 12, fontSize: 13, fontFamily: 'inherit', color: 'var(--fg)',
                        background: exclusionControlsDisabled ? 'var(--ink-100)' : 'white', border: '1px solid var(--border-default)', borderRadius: 10, outline: 'none',
                      }}
                    />
                  </label>
                  <Button kind="secondary" size="sm" icon={I.search} onClick={() => void searchExclusions(connection)} disabled={exclusionControlsDisabled || productSearch.loading} style={mobileActionStyle}>
                    {productSearch.loading ? t('stockAllocation.exclusions.searching') : t('stockAllocation.exclusions.search')}
                  </Button>
                </div>

                {productSearch.error !== null && (
                  <div style={{ marginTop: 10, color: 'var(--danger)', fontSize: 12.5, fontWeight: 800 }}>
                    {String(t('stockAllocation.exclusions.searchError'))}
                  </div>
                )}

                {productSearch.hasSearched && !productSearch.loading && productSearch.error === null && productSearch.results.length === 0 && (
                  <div style={{ marginTop: 10, color: 'var(--fg-muted)', fontSize: 12.5 }}>
                    {t(connection.provider === 'NOON' ? 'stockAllocation.exclusions.noonNoResults' : 'stockAllocation.exclusions.noResults')}
                  </div>
                )}

                {productSearch.results.length > 0 && (
                  <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
                    {productSearch.results.map(product => {
                      const selected = draft.excludedProducts.some(excluded => excluded.id === product.id)
                      return (
                        <div key={product.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: 10, borderRadius: 12, background: 'white', border: '1px solid var(--border-subtle)' }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--fg)', ...TEXT_WRAP_STYLE }}>{product.name}</div>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 3 }}>
                              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-muted)', ...TEXT_WRAP_STYLE }}>{product.sku}</span>
                              <Pill tone="neutral" size="sm">{t(`pages.products.status.${product.status}`)}</Pill>
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={selected || exclusionControlsDisabled}
                            aria-label={String(t(selected ? 'stockAllocation.exclusions.selectedAria' : 'stockAllocation.exclusions.addAria', { name: product.name }))}
                            onClick={() => addExcludedProduct(connection.id, product)}
                            style={{
                              justifySelf: isMobile ? 'stretch' : 'end',
                              fontFamily: 'inherit', fontSize: 12, fontWeight: 800, color: 'var(--brand-700)', background: selected ? 'transparent' : 'white',
                              border: selected ? '1px solid transparent' : '1px solid var(--brand-300)', borderRadius: 10, paddingBlock: 6, paddingInline: 10,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, cursor: selected || exclusionControlsDisabled ? 'not-allowed' : 'pointer', opacity: selected || exclusionControlsDisabled ? 0.5 : 1,
                              ...mobileActionStyle,
                            }}
                          >
                            <Icon size={13}>{selected ? I.check : I.plus}</Icon>
                            {selected ? t('stockAllocation.exclusions.selected') : t('stockAllocation.exclusions.add')}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? MOBILE_SINGLE_COLUMN : 'minmax(0, 1fr) auto', gap: 12, alignItems: 'center', marginTop: 16 }}>
                <div style={{ fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45, ...TEXT_WRAP_STYLE }}>
                  {t('stockAllocation.summary', {
                    percent: draft.allocationPercent || t('stockAllocation.emptyPercent'),
                    warehouses: selectedWarehouseNames(draft),
                  })}
                  {selectedOverAllocated.length > 0 && (
                    <div style={{ color: 'var(--warning)', fontWeight: 800, marginTop: 4 }}>
                      {t('stockAllocation.overAllocation.selectedWarning')}
                    </div>
                  )}
                </div>
                <Button kind="primary" size="sm" icon={I.check} onClick={() => void saveRule(connection)} disabled={saveDisabled} style={mobileActionStyle}>
                  {savingId === connection.id ? t('stockAllocation.actions.saving') : t('stockAllocation.actions.saveFor', { name: connection.displayName })}
                </Button>
              </div>

              {cardError !== undefined && (
                <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 12.5, fontWeight: 800 }}>
                  {String(t(errorMessageKey(cardError), { defaultValue: String(t('stockAllocation.errors.codes.REQUEST_FAILED')) }))}
                </div>
              )}
              {cardSaved && (
                <div style={{ marginTop: 12, color: 'var(--success)', fontSize: 12.5, fontWeight: 800 }}>
                  {t('stockAllocation.saved', { name: connection.displayName })}
                </div>
              )}
            </Card>
          )
        })}
      </div>
        </>
      )}
    </div>
  )
}
